import { sentryService } from '@/services/telemetry/sentry';

/**
 * Cap the number of missing-key breadcrumbs we send per locale. 24/7 kiosk
 * without refresh — unbounded Set growth would eventually OOM. 500 is
 * generous: typical locale has ~200 keys total.
 */
const MAX_LOGGED_MISSING_KEYS = 500;

/**
 * Proxy keys probed by Vue 3 reactivity / JSON serialization internals.
 * Without this guard, every render cycle logs spurious "missing key"
 * breadcrumbs for `__v_raw`, `__v_isRef`, etc.
 */
const INTERNAL_PROBE_KEYS = new Set<string>([
  'toJSON',
  'then',
  'valueOf',
  'toString',
  'Symbol(Symbol.toPrimitive)',
  '__v_raw',
  '__v_isRef',
  '__v_isReadonly',
  '__v_skip',
  '__v_isShallow',
]);

const loggedByLocale = new Map<string, Set<string>>();

/**
 * Reset logged-keys cache for the given locale. Call on every `setLocale(code)`
 * so that if a new user's key WAS missing under locale A but exists under
 * locale B, we re-detect it fresh in B.
 */
export function resetMissingKeyCache(localeCode?: string): void {
  if (localeCode) {
    loggedByLocale.delete(localeCode);
  } else {
    loggedByLocale.clear();
  }
}

/**
 * vue-i18n `missing` handler. Signature matches `createI18n({ missing })`.
 */
export function missingKeyHandler(
  locale: string,
  key: string,
  _instance?: unknown,
  _type?: string
): void {
  if (!locale || INTERNAL_PROBE_KEYS.has(key)) return;

  let seen = loggedByLocale.get(locale);
  if (!seen) {
    seen = new Set<string>();
    loggedByLocale.set(locale, seen);
  }
  if (seen.has(key) || seen.size >= MAX_LOGGED_MISSING_KEYS) return;
  seen.add(key);

  sentryService.addBreadcrumb(
    'localization',
    `Missing translation key: ${key}`,
    { key_path: key, locale },
    'warning'
  );
}
